import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { mobile, touchButton, mobileInput } from '../styles/MobileUtils';

interface MobileTaskEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: any) => void;
  initialTask?: any;
  householdMembers?: { name: string; age?: string; chores?: string[]; color?: string; }[];
  profileData?: any;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.md};
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: ${theme.spacing.xl};
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  transform: scale(0.9);
  transition: transform 0.3s ease;
  
  ${ModalOverlay}[isOpen] & {
    transform: scale(1);
  }
  
  ${mobile(`
    border-radius: 16px;
    padding: ${theme.spacing.lg};
    margin: ${theme.spacing.sm};
  `)}
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const Title = styled.h2`
  color: ${theme.colors.text};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  ${touchButton}
  background: none;
  border: none;
  color: ${theme.colors.lightText};
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  color: ${theme.colors.text};
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  ${mobileInput}
  padding: ${theme.spacing.md};
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
  }
`;

const TextArea = styled.textarea`
  ${mobileInput}
  padding: ${theme.spacing.md};
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s ease;
  font-family: ${theme.fonts.main};
  
  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
  }
`;

const Select = styled.select`
  ${mobileInput}
  padding: ${theme.spacing.md};
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.2s ease;
  
  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
  }
`;

const TimeInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};
`;

const ColorSelector = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  margin-top: ${theme.spacing.sm};
`;

const ColorOption = styled.button<{ color: string; selected: boolean }>`
  ${touchButton}
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid ${props => props.selected ? 'white' : 'transparent'};
  background: ${props => props.color};
  box-shadow: ${props => props.selected ? '0 0 0 2px ' + props.color : '0 2px 4px rgba(0, 0, 0, 0.1)'};
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.95);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  ${touchButton}
  flex: 1;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.primary};
    color: white;
    
    &:hover {
      background: ${theme.colors.secondary};
    }
  ` : `
    background: rgba(0, 0, 0, 0.1);
    color: ${theme.colors.text};
    
    &:hover {
      background: rgba(0, 0, 0, 0.15);
    }
  `}
  
  &:active {
    transform: scale(0.98);
  }
`;

const MobileTaskEditor: React.FC<MobileTaskEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  initialTask,
  householdMembers = [],
  profileData
}) => {
  const [formData, setFormData] = useState({
    title: '',
    note: '',
    date: '',
    startTime: '',
    endTime: '',
    color: '#F7AD79',
    type: 'chore',
    assignedTo: ''
  });

  // Available activities based on task type and assigned member
  const getAvailableActivities = (taskType: string, assignedMember?: string) => {
    if (!profileData) {
      return [];
    }
    
    switch (taskType) {
      case 'chore':
        // For chores, get the assigned chores from the household member's profile
        let chores: string[] = [];
        
        // Try to find chore assignments in different possible locations
        let choreAssignments = null;
        
        if (profileData.messTestData?.choreAssignments && Array.isArray(profileData.messTestData.choreAssignments)) {
          choreAssignments = profileData.messTestData.choreAssignments;
        } else if (profileData.choreAssignments && Array.isArray(profileData.choreAssignments)) {
          choreAssignments = profileData.choreAssignments;
        } else if (profileData.chore?.assignments && Array.isArray(profileData.chore.assignments)) {
          choreAssignments = profileData.chore.assignments;
        } else if (profileData.chores?.assignments && Array.isArray(profileData.chores.assignments)) {
          choreAssignments = profileData.chores.assignments;
        }
        
        if (choreAssignments) {
          // Filter chores by assigned member
          if (assignedMember) {
            // Get chores assigned to this specific member
            const memberChores: string[] = [];
            choreAssignments.forEach((ca: any) => {
              if (ca.member === assignedMember && ca.chores && Array.isArray(ca.chores)) {
                memberChores.push(...ca.chores);
              }
            });
            
            // Remove duplicates
            chores = memberChores.filter((chore: string, index: number) => memberChores.indexOf(chore) === index);
          } else {
            // No member assigned, show all available chores
            const allChores: string[] = [];
            choreAssignments.forEach((ca: any) => {
              if (ca.chores && Array.isArray(ca.chores)) {
                allChores.push(...ca.chores);
              }
            });
            chores = allChores.filter((chore: string, index: number) => allChores.indexOf(chore) === index);
          }
        }
        
        return chores;
        
      case 'lunchbox':
        let lunchboxMeals = [];
        
        if (profileData.food?.lunchboxIdeas && Array.isArray(profileData.food.lunchboxIdeas)) {
          lunchboxMeals = profileData.food.lunchboxIdeas;
        } else if (profileData.lunchboxMeals && Array.isArray(profileData.lunchboxMeals)) {
          lunchboxMeals = profileData.lunchboxMeals;
        } else {
          // Default lunchbox options
          lunchboxMeals = [
            'Sandwich',
            'Salad',
            'Leftovers',
            'Soup',
            'Wrap',
            'Pasta'
          ];
        }
        
        return lunchboxMeals;
        
      case 'dinner':
        let dinnerMeals = [];
        
        if (profileData.food?.favoriteMeals && Array.isArray(profileData.food.favoriteMeals)) {
          dinnerMeals = profileData.food.favoriteMeals;
        } else if (profileData.dinnerMeals && Array.isArray(profileData.dinnerMeals)) {
          dinnerMeals = profileData.dinnerMeals;
        } else {
          // Default dinner options
          dinnerMeals = [
            'Pasta',
            'Rice and vegetables',
            'Chicken dinner',
            'Takeaway',
            'Pizza',
            'Stir fry',
            'Soup and bread'
          ];
        }
        
        return dinnerMeals;
        
      case 'event':
        return []; // Events use custom text input
        
      default:
        return [];
    }
  };



  useEffect(() => {
    if (initialTask) {
      setFormData({
        title: initialTask.title || '',
        note: initialTask.note || '',
        date: initialTask.date || new Date().toISOString().split('T')[0],
        startTime: initialTask.startTime || initialTask.time || '',
        endTime: initialTask.endTime || '',
        color: initialTask.color || '#F7AD79',
        type: initialTask.type || 'chore',
        assignedTo: initialTask.assignedTo || ''
      });
    } else {
      setFormData({
        title: '',
        note: '',
        date: new Date().toISOString().split('T')[0],
        startTime: '',
        endTime: '',
        color: '#F7AD79',
        type: 'chore',
        assignedTo: ''
      });
    }
  }, [initialTask, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle assigned member change - set color and reset activity
    if (name === 'assignedTo') {
      const selectedMember = householdMembers.find(member => member.name === value);
      const memberColor = selectedMember?.color || '#6B7280';
      
      setFormData(prev => ({ 
        ...prev, 
        [name]: value, 
        color: memberColor,
        title: '' // Reset activity when member changes
      }));
    }
    // Reset activity when task type changes
    else if (name === 'type') {
      setFormData(prev => ({ ...prev, [name]: value, title: '' }));
    } 
    // Handle all other input changes
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };



  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Format the data to match what the backend expects
    const taskData = {
      ...formData,
      assignedColor: formData.color // Map color to assignedColor for consistency
    };
    onSave(taskData);
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent>
        <Header>
          <Title>{initialTask ? 'Edit Task' : 'New Task'}</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Date</Label>
            <Input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Time</Label>
            <TimeInputGroup>
              <div>
                <Label style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Start Time</Label>
                <Input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  placeholder="Start time"
                />
              </div>
              <div>
                <Label style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>End Time</Label>
                <Input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  placeholder="End time"
                />
              </div>
            </TimeInputGroup>
          </FormGroup>

          <FormGroup>
            <Label>Assigned To</Label>
            <Select
              name="assignedTo"
              value={formData.assignedTo}
              onChange={handleInputChange}
            >
              <option value="">Unassigned</option>
              {householdMembers.map((member) => (
                <option key={member.name} value={member.name}>
                  {member.name}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Task Type</Label>
            <Select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              required
            >
              <option value="chore">Task/Chore</option>
              <option value="lunchbox">Lunchbox</option>
              <option value="dinner">Dinner</option>
              <option value="event">Event</option>
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label>Activity</Label>
            {formData.type === 'event' ? (
              <Input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter event description"
                required
              />
            ) : (
              <Select
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              >
                <option value="">Select activity</option>
                {getAvailableActivities(formData.type, formData.assignedTo).map((activity: string) => (
                  <option key={activity} value={activity}>
                    {activity}
                  </option>
                ))}
              </Select>
            )}
          </FormGroup>
          
          <FormGroup>
            <Label>Note</Label>
            <TextArea
              name="note"
              value={formData.note}
              onChange={handleInputChange}
              placeholder="Add a note..."
            />
          </FormGroup>
          
          <ButtonGroup>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Save
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MobileTaskEditor; 